import * as jsts from "jsts";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateLines, CreateLineSystem } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { CreateRibbon } from "@babylonjs/core/Meshes/Builders/ribbonBuilder";
import { ExtrudeShape } from "@babylonjs/core/Meshes/Builders/shapeBuilder";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Vector2, Vector3, Matrix } from "@babylonjs/core/Maths/math.vector";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { ElementGeometry } from "./ElementGeometry";
import { RoomManager } from './../../RoomManager';
import { Util } from "../../utils/Util";
import { PropertyValue, NumberProperty } from "@egr/wcf/modules/core/prop";
import { ShadowGenerator } from "@egr/wcf/modules/core/rendering/ShadowGenerator";
import { newError } from '@egr/wcf/modules/utils/error';
import { radToDeg } from "@egr/wcf/modules/utils/math";
import { Log } from '@egr/wcf/modules/utils';
import { toLocaleString } from '@egr/wcf/modules/utils/number';
import { signedAngleBetweenVectors2 } from "@egr/wcf/modules/core/utils/MathUtils";
import { updateFrozenWorldMatrix } from '@egr/wcf/modules/core/utils/MeshUtils';
export class Line extends ElementGeometry {
    constructor(pRoomManager, pElement) {
        super(pRoomManager, pElement);
        this.mRoomElement = pElement;
        this.mRoomManager = pRoomManager;
        this.mPoints = new Array();
        this.mPointAngles = new Array();
        this.mMidPoints = new Array();
        this.mCutPoints = new Array();
        this.mWidth = 0;
        this.mDepth = 0;
        this.mMaterial = 0;
        this.mColor = "#999999";
        this.mCenter = Vector3.Zero();
        this.mminPoint = Vector3.Zero();
        this.mmaxPoint = Vector3.Zero();
        this.mArea = 0;
        this.mPerimeter = 0;
        this.mClosedPolygon = false;
        this.mHillPoints_Offset = null;
        this.mHillPoints_Base = null;
        this.mcanCreateHill = false;
        this.mInputWasClockwise = false;
        this.mCornerAnglesRemoved = true;
        this.mRoomElement.Params.ElementPoints = new Array();
        this.mRoomElement.Params.ElementOriginalPoints = new Array();
        this.mRoomElement.Params.material = this.mRoomElement.material;
        this.mRoomElement.Params.materialColor = this.getInitialParams().color;
        this.mRoomManager.app.viewer.scene.onBeforeCameraRenderObservable.add(this.onBeforeCameraChanged(this.mRoomElement));
        this.mTextPlanes = [];
    }
    getType() {
        return ("Line");
    }
    setMesh(pMesh) {
        this.mArea = Util.calcMeshArea(pMesh) * 0.25;
    }
    get drawnMesh() {
        return (this.mMesh2d);
    }
    getPoints() {
        return this.mPoints;
    }
    get inputWasClockwise() {
        return (this.mInputWasClockwise);
    }
    getPrevLine(pIndex) {
        if (this.mPointAngles.length < 3) {
            return (null);
        }
        let tRes = {};
        // when editing points after drawing
        if (pIndex !== undefined) {
            return (this._getPrevLine(pIndex));
        }
        if (this.mInputWasClockwise) {
            const tcwSP = this.mPointAngles.length === 3 ? 0 : 3;
            const tcwEP = 2;
            tRes.Start = this.mPointAngles[tcwSP].Origin.clone();
            tRes.End = this.mPointAngles[tcwEP].Origin.clone();
            return (tRes);
        }
        const tccwSP = this.mPointAngles.length - 3;
        const tccwEP = this.mPointAngles.length - 2;
        tRes.Start = this.mPointAngles[tccwSP].Origin.clone();
        tRes.End = this.mPointAngles[tccwEP].Origin.clone();
        return (tRes);
    }
    getProperties() {
        let tProperties = new Array();
        if (this.mRoomElement.type !== "Line" || this.mRoomElement.GridRE_REF == null) {
            return (tProperties);
        }
        const tGFD = this.mRoomElement.gridFieldsData;
        // grid full fields
        let tgridFullProp = new NumberProperty(this.mRoomElement, Line.PROP_KEY_GRID_FULL, 'Full');
        tgridFullProp.class = "base";
        tgridFullProp.editable = false;
        tgridFullProp.numDecDigits = 0;
        tgridFullProp.getValue = () => new PropertyValue(tGFD.full);
        tProperties.push(tgridFullProp);
        // grid diagonal fields
        let tgridDiagonalProp = new NumberProperty(this.mRoomElement, Line.PROP_KEY_GRID_DIAGONAL, 'Diagonal');
        tgridDiagonalProp.class = "base";
        tgridDiagonalProp.editable = false;
        tgridDiagonalProp.numDecDigits = 0;
        tgridDiagonalProp.getValue = () => new PropertyValue(tGFD.diagonal);
        tProperties.push(tgridDiagonalProp);
        // grid vertical fields
        let tgridVerticalProp = new NumberProperty(this.mRoomElement, Line.PROP_KEY_GRID_VERTICAL, 'Vertical');
        tgridVerticalProp.class = "base";
        tgridVerticalProp.editable = false;
        tgridVerticalProp.numDecDigits = 0;
        tgridVerticalProp.getValue = () => new PropertyValue(tGFD.vertical);
        tProperties.push(tgridVerticalProp);
        // grid horizontal fields
        let tgridHorizontalProp = new NumberProperty(this.mRoomElement, Line.PROP_KEY_GRID_HORIZONTAL, 'Horizontal');
        tgridHorizontalProp.class = "base";
        tgridHorizontalProp.editable = false;
        tgridHorizontalProp.numDecDigits = 0;
        tgridHorizontalProp.getValue = () => new PropertyValue(tGFD.horizontal);
        tProperties.push(tgridHorizontalProp);
        // grid freeform fields
        let tgridFreeformProp = new NumberProperty(this.mRoomElement, Line.PROP_KEY_GRID_FREEFORM, 'Freeform');
        tgridFreeformProp.class = "base";
        tgridFreeformProp.editable = false;
        tgridFreeformProp.numDecDigits = 0;
        tgridFreeformProp.getValue = () => new PropertyValue(tGFD.freeform);
        tProperties.push(tgridFreeformProp);
        // grid total full
        let ttotalFull = tGFD.full;
        ttotalFull += Math.round(tGFD.diagonal / 2);
        ttotalFull += Math.round(tGFD.vertical / 2);
        ttotalFull += Math.round(tGFD.horizontal / 2);
        ttotalFull += tGFD.freeform;
        let tgridTotalFullProp = new NumberProperty(this.mRoomElement, Line.PROP_KEY_GRID_TOTALFULL, 'Total full');
        tgridTotalFullProp.class = "base";
        tgridTotalFullProp.editable = false;
        tgridTotalFullProp.numDecDigits = 0;
        tgridTotalFullProp.getValue = () => new PropertyValue(ttotalFull);
        tProperties.push(tgridTotalFullProp);
        return (tProperties);
    }
    getCurrLine(pIndex) {
        if (this.mPointAngles.length < 3) {
            return (null);
        }
        let tRes = {};
        const tarrMaxLen = this.mPointAngles.length - 1;
        let tccwSP = -1;
        let tccwEP = -1;
        if (pIndex === tarrMaxLen) {
            tccwSP = 0;
            tccwEP = tarrMaxLen;
        }
        else {
            tccwSP = pIndex + 1;
            tccwEP = pIndex;
        }
        tRes.Start = this.mPointAngles[tccwSP].Origin.clone();
        tRes.End = this.mPointAngles[tccwEP].Origin.clone();
        return (tRes);
    }
    getMidPoints() {
        this.mMidPoints = [];
        for (let i = 0; i < this.mPoints.length - 1; i++) {
            let tmP = Vector3.Center(this.mPoints[i], this.mPoints[i + 1]);
            this.mMidPoints.push(tmP);
        }
        this.mMidPoints.push(Vector3.Center(this.mPoints[0], this.mPoints[this.mPoints.length - 1]));
        return (this.mMidPoints);
    }
    getPolygon(pOffset = true) {
        let tpolygonPoints = [];
        for (let tP of this.mPoints) {
            let tnodePos = new Vector3(0, 0, 0);
            if (pOffset)
                tnodePos = this.mRoomElement.position.clone();
            let tnP = tP.add(tnodePos);
            tpolygonPoints.push(tnP);
        }
        return (tpolygonPoints);
    }
    getTransformNodeArr() {
        return (this.mPointAngles);
    }
    width() {
        return this.mWidth;
    }
    depth() {
        return this.mDepth;
    }
    material() {
        return this.mMaterial;
    }
    center() {
        return this.mCenter;
    }
    minPoint() {
        return this.mminPoint;
    }
    maxPoint() {
        return this.mmaxPoint;
    }
    setAnglesVisibility(pInit, pVisible) {
        if (pVisible) {
            if (pInit) {
                this.calcPointAngles();
                this.initCornerAngleData();
            }
            this.updateCornerAngles();
            return;
        }
        this.hideCornerAngles();
    }
    hideCornerAngles(pdispose = false) {
        this.removeAngleArcMesh(pdispose);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    updateCornerAngles() {
        if (this.mRoomElement.node == undefined ||
            (this.mRoomElement.type === "Line" && this.mRoomElement.GridRE_REF != null)) {
            return;
        }
        let tWM = new Matrix();
        try {
            tWM = this.mRoomElement.node.computeWorldMatrix();
        }
        catch (pError) {
            Log.error("Failed to get world Matrix from node.", pError);
            return;
        }
        for (let tEAD of this.mPointAngles) {
            let tbiSecPoint = tEAD.CornerBiSector.scale(0.15);
            let tcurrPoint = this.mPoints[tEAD.index].add(tbiSecPoint);
            if (tEAD.node == null) {
                tEAD.node = new TransformNode("lineArcNode", this.mScene);
            }
            const twmPos = Vector3.TransformCoordinates(tcurrPoint, tWM);
            tEAD.node.setAbsolutePosition(twmPos);
            updateFrozenWorldMatrix(tEAD.node, true);
            const tScaleFactor = Util.getScaleFactor(this.mRoomManager, tEAD.node, 12); // this.getScaleFactor();
            if (this.mCornerAngleData == null ||
                this.mCornerAngleData.length === 0 ||
                tEAD.index > this.mCornerAngleData.length - 1) {
                continue;
            }
            let tEAData = this.mCornerAngleData[tEAD.index];
            if (tEAData.TextPlane == null) {
                continue;
            }
            if (tScaleFactor != null) {
                tEAData.TextPlane.scaling = new Vector3(tScaleFactor, tScaleFactor, tScaleFactor);
                tEAData.TextPlane.isVisible = true;
                let tPos = Vector3.TransformCoordinates(tEAD.Origin, tWM);
                if (tEAD.CornerBiSector != null) {
                    tPos = tPos.add(tEAD.CornerBiSector.scale(tScaleFactor * 0.22));
                    tPos.y = 0.002;
                    tEAData.TextPlane.setAbsolutePosition(tPos);
                }
            }
        }
    }
    showInteractors(pShow) {
        if (this.mInteractors != null)
            this.mInteractors.showInteractors(pShow);
    }
    correctDirection(pCreateNew) {
        let tClockwise = false;
        let tPoints = this.mPoints.slice();
        // if (!Util.vectorEquals3(tPoints[0], tPoints[tPoints.length - 1])) {
        //     tPoints.push(tPoints[0]);
        // }
        tClockwise = Util._clockWise3(tPoints);
        if (!tClockwise) {
            this.mInputWasClockwise = false;
            return;
        }
        this.mInputWasClockwise = true;
        let tpntTmp = new Array();
        const tP0 = tPoints.shift();
        if (tP0 == null) {
            return;
        }
        tpntTmp.push(tP0);
        while (tPoints.length > 0) {
            const tPlast = tPoints.pop();
            if (tPlast != null) {
                tpntTmp.push(tPlast);
            }
            else {
                return;
            }
        }
        this.mPoints = tpntTmp.slice();
        return;
        // if (tClockwise && !pCreateNew) {
        //     this.mPoints.push(this.mPoints[0]);
        //     this.mPoints.reverse();
        //     this.mPoints.pop();
        // }
    }
    correctDirection1() {
        for (let tPolygon of this.mCutPoints) {
            let tClockwise = Util._clockWise3(tPolygon);
            if (tClockwise)
                tPolygon.reverse();
        }
    }
    getInitialParams() {
        let tParams = {};
        tParams.width = 0.5;
        tParams.height = 0;
        tParams.floorOffset = 0.0;
        tParams.color = this.mColor;
        return (tParams);
    }
    getparent() {
        return (this.mParent);
    }
    get Area() {
        return (this.mArea);
    }
    get Perimeter() {
        return (this.mPerimeter);
    }
    getLines() {
        let tRes = new Array();
        let tlen = this.mRoomElement.Params.ElementPoints.length;
        for (let i = 0; i < this.mRoomElement.Params.ElementPoints.length - 1; i++) {
            tRes.push([this.mRoomElement.Params.ElementPoints[i], this.mRoomElement.Params.ElementPoints[i + 1]]);
        }
        tRes.push([this.mRoomElement.Params.ElementPoints[tlen - 1], this.mRoomElement.Params.ElementPoints[0]]);
        return (tRes);
    }
    create2dGeometry(pParent) {
        this.calcPointAngles();
        this.initCornerAngleData();
        this.mParent = pParent;
        let tLine = this.createLine(pParent);
        let tHill = this.createHill(pParent);
        this.createCornerAngles();
        if (tLine != null) {
            this.mRoomElement.setMesh2d(tLine);
        }
        this.mergeShapeMeshes(pParent, this.mRoomElement.setMesh2d);
    }
    create3dGeometry(pParent) {
        let tLine = this.createLine(pParent);
        let tHill = this.createHill(pParent);
        if (tLine != null) {
            this.mRoomElement.setMesh3d(tLine);
        }
        this.mergeShapeMeshes(pParent, this.mRoomElement.setMesh3d);
    }
    createAdditionalGeometry(pParent) {
        this.createHelpLines(pParent);
    }
    createSpecialGeometry(pParent) {
        if (this.mRoomElement.crossHairVisible)
            this.createCrossHair(pParent);
    }
    serializeIOData() {
        return (this.mRoomElement.Params);
    }
    loadIOData(pData) {
        let tParams = pData;
        let tPoints = new Array();
        for (let tEP of tParams.ElementPoints) {
            let tP = new Vector3(0, 0, 0);
            tP.x = tEP.x;
            tP.z = tEP.z;
            tPoints.push(tP);
        }
        this.setPoints(tPoints, false);
    }
    setPoints(pPoints, pCreateNew = false) {
        this.mPoints = [];
        for (let tPoint of pPoints) {
            let tP = new Vector3(0, 0, 0);
            tP.x = tPoint.x;
            tP.z = tPoint.z;
            this.mPoints.push(tP);
        }
        if (this.mPoints.length > 2) {
            this.mClosedPolygon = true;
            this.mRoomElement.setClosedPolygon(this.mClosedPolygon);
        }
        this.correctDirection(pCreateNew);
        if (this.mPoints.length > 3 && Util.vectorEquals3(this.mPoints[1], this.mPoints[2])) {
            this.mPoints.splice(1, 1);
        }
        this.mRoomElement.Params.ElementPoints = this.mPoints;
        this.calcCenter();
        this.calcSize();
    }
    deletePoint(pIndex) {
        if (pIndex > this.mPoints.length - 1 || pIndex < 0 || this.mPoints.length === 0 || this.mPoints.length <= 3)
            return;
        this.mPoints.splice(pIndex, 1);
        this.mRoomElement.Params.ElementPoints = this.mPoints;
        this.calcCenter();
        this.calcSize();
    }
    addPoint(pIndex) {
        let tc1 = pIndex - 1;
        let tc2 = pIndex === this.mPoints.length ? 0 : pIndex;
        let tnewPoint = Vector3.Center(this.mPoints[tc1], this.mPoints[tc2]);
        this.mPoints.splice(pIndex, 0, tnewPoint);
        this.mRoomElement.Params.ElementPoints = this.mPoints;
        this.calcCenter();
        this.calcSize();
    }
    setCutPoints(pPoints, pCenter = false) {
        this.mCutPoints = [];
        for (let tarrPoints of pPoints) {
            let tnewPoints = new Array();
            for (let tPoint of tarrPoints) {
                let tP = new Vector3(0, 0, 0);
                tP.x = tPoint.x;
                tP.z = tPoint.z;
                tnewPoints.push(tP);
            }
            this.mCutPoints.push(tnewPoints);
        }
        if (pCenter)
            this.centerPoints(this.mCutPoints);
        this.checkClosedPolygon();
        if (this.mClosedPolygon)
            this.correctDirection1();
    }
    onBeforeCameraChanged(pRoomElement) {
        return () => {
            if (pRoomElement.isSelected || pRoomElement.isDrawingNew) {
                if (this.mCornerAnglesRemoved) {
                    this.initCornerAngleData();
                    this.createCornerAngles();
                }
                else {
                    this.updateCornerAngles();
                }
            }
            else {
                this.hideCornerAngles();
                this.mCornerAnglesRemoved = true;
            }
        };
    }
    checkClosedPolygon() {
        if (this.mPoints.length < 3)
            return;
        this.mClosedPolygon = Util.closedPolygon(this.mPoints);
        this.mRoomElement.setClosedPolygon(this.mClosedPolygon);
    }
    calcPointAngles() {
        // clear list complete ? XXX
        for (let tEAD of this.mPointAngles) {
            if (tEAD.node != null) {
                tEAD.node.dispose();
            }
            tEAD.node = null;
        }
        this.mPointAngles = [];
        if (this.mPoints.length < 3) {
            return;
        }
        // inner points
        for (let i = 0; i < this.mPoints.length; i++) {
            const tCornerData = {};
            let tnextP = i + 1;
            let tprevP = i - 1;
            if (i === this.mPoints.length - 1) {
                tnextP = 0;
            }
            if (i === 0) {
                tprevP = this.mPoints.length - 1;
            }
            const tLineLeft_raw = Util.to2d(this.mPoints[tnextP].subtract(this.mPoints[i]));
            const tLineRight_raw = Util.to2d(this.mPoints[tprevP].subtract(this.mPoints[i]));
            const tLineLeft = (tLineLeft_raw).normalize();
            const tLineRight = (tLineRight_raw).normalize();
            const tLineLeft_add = tLineLeft.add(tLineRight);
            const tBiSector = tLineLeft_add.normalize();
            let tangle = radToDeg(signedAngleBetweenVectors2(tLineLeft, tLineRight));
            tCornerData.index = i;
            tCornerData.Direction1 = Util.to3d(tLineLeft);
            tCornerData.Direction2 = Util.to3d(tLineRight);
            tCornerData.Origin = this.mPoints[i].clone();
            tCornerData.CornerBiSector = Util.to3d(tBiSector);
            if (tangle < 0) {
                tangle = 360 + tangle;
                tCornerData.CornerBiSector = Util.to3d(Util.invert2d(tBiSector));
            }
            tCornerData.CornerAngle = tangle;
            this.mPointAngles.push(tCornerData);
        }
    }
    centerPoints(pPoints) {
        this.mCutPoints = [];
        for (let tpointArr of pPoints) {
            let tnewArr = new Array();
            for (let tPoint of tpointArr) {
                let tnP = Util.to3d(Util.to2d(Vector3.TransformCoordinates(tPoint, this.mRoomElement.transformInverted)));
                tnewArr.push(tnP);
            }
            this.mCutPoints.push(tnewArr);
        }
    }
    createCornerAngleData(pIdx) {
        let tcornerAngleData = {};
        return (tcornerAngleData);
    }
    initCornerAngleData() {
        if (this.mCornerAngleData == null) {
            this.mCornerAngleData = Array();
        }
        if (this.mCornerAngleData.length < this.mPointAngles.length) {
            const tdiff = this.mPointAngles.length - this.mCornerAngleData.length;
            for (let i = 0; i < tdiff; i++) {
                const tEAD = this.createCornerAngleData(i);
                this.mCornerAngleData.push(tEAD);
            }
        }
    }
    removeAngleArcMesh(pdispose = false) {
        if (this.mCornerAngleData == null || this.mCornerAngleData.length === 0) {
            return;
        }
        while (this.mCornerAngleData.length > 0) {
            const tEAD = this.mCornerAngleData.pop();
            if (tEAD != null) {
                if (tEAD.TextPlane != null) {
                    tEAD.TextPlane.setEnabled(false);
                    tEAD.TextPlane.dispose();
                }
            }
        }
        this.mCornerAngleData = [];
    }
    calcCenter() {
        let t2dPoints = new Array();
        for (let tP of this.mPoints)
            t2dPoints.push(new Vector2(tP.x, tP.z));
        let tCenter = new Vector3(0, 0, 0);
        if (this.mPoints.length > 2)
            tCenter = Util.getPolygonCentroid(t2dPoints);
        else {
            tCenter = Vector3.Center(this.mPoints[0], this.mPoints[1]);
        }
        this.mCenter = tCenter;
    }
    calcSize() {
        if (this.mPoints.length === 0)
            return;
        let tminX = this.mPoints[0].x;
        let tminY = this.mPoints[0].z;
        let tmaxX = this.mPoints[0].x;
        let tmaxY = this.mPoints[0].z;
        for (let tPoint of this.mPoints) {
            tminX = Math.min(tPoint.x, tminX);
            tmaxX = Math.max(tPoint.x, tmaxX);
            tminY = Math.min(tPoint.z, tminY);
            tmaxY = Math.max(tPoint.z, tmaxY);
        }
        this.mWidth = tmaxX - tminX;
        this.mDepth = tmaxY - tminY;
        if (this.mRoomElement.GridRE_REF == null) {
            this.mRoomElement.setWidth(this.mWidth, false, false);
            this.mRoomElement.setDepth(this.mDepth, false, false);
        }
        this.mminPoint = new Vector3(tminX, 0, tminY);
        this.mmaxPoint = new Vector3(tmaxX, 0, tmaxY);
        this.calcLenght();
    }
    calcLenght() {
        let tDist = 0;
        for (let i = 1; i < this.mPoints.length; i++) {
            tDist += Vector3.Distance(this.mPoints[i - 1], this.mPoints[i]);
        }
        this.mPerimeter = tDist;
    }
    drawOutline(pParent) {
        //for invisible picking plane
        let tpickShape = [
            new Vector3(-0.5, 0, 0.5),
            new Vector3(-0.5, 0, -0.5),
            new Vector3(0.5, 0, -0.5),
            new Vector3(0.5, 0, 0.5),
            new Vector3(-0.5, 0, 0.5)
        ];
        let tPoints = new Array();
        for (let tP of this.mRoomElement.Params.ElementPoints) {
            tPoints.push(tP);
        }
        if (!Util.vectorEquals3(tPoints[0], tPoints[tPoints.length - 1])) {
            tPoints.push(tPoints[0]);
        }
        if (tPoints.length > 2 && (Util.vectorEquals3(tPoints[0], tPoints[1]))) {
            tPoints.splice(0, 1);
        }
        const tmeshName = RoomManager.ROOM_SHAPE2D_PREFIX + this.mRoomElement.type.toString();
        //if (this.mRoomElement.material == -1){
        let tLines = CreateLines(tmeshName + RoomManager.ROOM_SHAPE2D_SUFFIXOUTLINE, { points: tPoints }, this.mScene);
        tLines.color = Color3.FromHexString("#FF0000");
        tLines.parent = pParent;
        tLines.isPickable = false;
        tLines.renderingGroupId = 3;
        ShadowGenerator.disableMeshShadow(tLines);
        this.prepare3dMesh(tLines);
        //}
        let tpolygon = ExtrudeShape("", { shape: tpickShape,
            path: this.mRoomElement.Params.ElementPoints,
            sideOrientation: Mesh.DOUBLESIDE,
            updatable: true }, this.mScene);
        tpolygon.isPickable = true;
        tpolygon.name = tmeshName + RoomManager.ROOM_SHAPE2D_SUFFIXPICKLINE;
        tpolygon.parent = pParent;
        tpolygon.visibility = 0;
        ShadowGenerator.disableMeshShadow(tpolygon);
        this.prepare3dMesh(tpolygon);
        return (tpolygon);
    }
    getCorrectedPolyPath() {
        let tP = this.getPolygon(false);
        if (Util.vectorEquals3(tP[0], tP[tP.length - 1]))
            tP.pop();
        if ((tP.length > 3) && (Util.vectorEquals3(tP[tP.length - 1], tP[tP.length - 2]))) {
            tP.pop();
        }
        tP = Util.correctPolygonDirection3(tP);
        return (tP);
    }
    _getPrevLine(pIndex) {
        let tRes = {};
        const tarrMaxLen = this.mPointAngles.length - 1;
        let tccwSP = -1;
        let tccwEP = -1;
        if (pIndex === tarrMaxLen) {
            tccwSP = 1;
            tccwEP = 0;
        }
        else if (pIndex === tarrMaxLen - 1) {
            tccwSP = 0;
            tccwEP = tarrMaxLen;
        }
        else {
            tccwSP = pIndex + 2;
            tccwEP = pIndex + 1;
        }
        tRes.Start = this.mPointAngles[tccwSP].Origin.clone();
        tRes.End = this.mPointAngles[tccwEP].Origin.clone();
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    createOffsetCSG(pBasePoly) {
        let tRes = {};
        tRes.CAG = null;
        tRes.CSG = null;
        const tHillOffset = this.mRoomElement.Params.hillOffset;
        const tHillType = this.mRoomElement.Params.terrainType;
        if (tHillType === "Flat") {
            return (tRes);
        }
        let tbasePolygon = pBasePoly;
        let toffsetPoints = offset(tbasePolygon, (tHillType === 'Top' ? tHillOffset : -tHillOffset));
        if (toffsetPoints.length !== tbasePolygon.length) {
            return (tRes);
        }
        // offset plane
        let tPointsOffset = new Array();
        let tCAGOffset = null;
        toffsetPoints = Util.correctPolygonDirection3(toffsetPoints);
        this.limitOffsetDistance();
        if (tHillType === 'Bottom') {
            for (let tP1 of toffsetPoints) {
                tP1.addInPlace(this.mRoomElement.Params.hillTopOffset);
            }
        }
        if (toffsetPoints.length === tbasePolygon.length) {
            this.mHillPoints_Offset = toffsetPoints;
        }
        for (let tPoint of toffsetPoints) {
            tPointsOffset.push(new CSG.Vector2D(tPoint.x, tPoint.z));
        }
        if (toffsetPoints.length === tbasePolygon.length) {
            tCAGOffset = CAG.fromPointsNoCheck(tPointsOffset);
        }
        if (tCAGOffset == null || (tCAGOffset.isSelfIntersecting())) {
            return (tRes);
        }
        let tCSGOffset = Util.CAG2CSG(tCAGOffset);
        tRes.CAG = tCAGOffset;
        tRes.CSG = tCSGOffset;
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    createCSG(pParent) {
        const tHillHeight = this.mRoomElement.Params.hillHeight;
        const tHillType = this.mRoomElement.Params.terrainType;
        let tbasePolygon = this.getCorrectedPolyPath();
        this.mHillPoints_Base = tbasePolygon;
        let tCAGCSGOffset = this.createOffsetCSG(tbasePolygon);
        if (tHillType === 'Top') {
            for (let tP1 of tbasePolygon) {
                tP1.addInPlace(this.mRoomElement.Params.hillTopOffset);
            }
        }
        // hill base plane / initial 2d shape
        let tPoints = new Array();
        for (let tPoint of tbasePolygon) {
            tPoints.push(new CSG.Vector2D(tPoint.x, tPoint.z));
        }
        let tCAGBase = CAG.fromPointsNoCheck(tPoints);
        if (tCAGBase.isSelfIntersecting()) {
            this.mRoomElement.setCAG(null);
            return (null);
        }
        this.mcanCreateHill = false;
        let tCSGBase = null;
        tCSGBase = Util.CAG2CSG(tCAGBase);
        this.mRoomElement.setCAG(tCAGBase);
        this.mRoomElement.setCAGoriginal(tCAGBase);
        if (tCAGCSGOffset.CAG != null && (!tCAGCSGOffset.CAG.isSelfIntersecting()) && (tHillType !== 'Flat')) {
            if (tCAGCSGOffset.CSG != null) {
                this.mcanCreateHill = true;
            }
            if (tHillType === "Top") {
                tCSGBase = tCSGBase.translate([0, tHillHeight, 0]);
                this.mRoomElement.setCAG(tCAGCSGOffset.CAG);
                this.mRoomElement.setCAGoriginal(tCAGCSGOffset.CAG);
            }
            if (tHillType === "Bottom" && tCAGCSGOffset.CSG != null) {
                tCAGCSGOffset.CSG = tCAGCSGOffset.CSG.translate([0, tHillHeight, 0]);
                this.mcanCreateHill = true;
            }
        }
        let tMeshes_base = null;
        let tMeshes_offset = null;
        switch (tHillType) {
            case 'Flat':
                tMeshes_base = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'line', tCSGBase, this.mRoomElement.localMaterial);
                if (tMeshes_base == null) {
                    return (null);
                }
                return (tMeshes_base[0]);
            case 'Bottom':
                tMeshes_base = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'line', tCSGBase, this.mRoomElement.localMaterial);
                tMeshes_offset = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'line', tCAGCSGOffset.CSG, this.mRoomElement.localMaterial);
                if (tMeshes_base == null) {
                    return (null);
                }
                return (tMeshes_base[0]);
            case 'Top':
                tMeshes_base = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'line', tCSGBase, this.mRoomElement.localMaterial);
                tMeshes_offset = this.createMeshOfCSG(pParent, RoomManager.ROOM_SHAPE2D_PREFIX + 'line', tCAGCSGOffset.CSG, this.mRoomElement.localMaterial);
                if (tMeshes_offset == null) {
                    return (null);
                }
                return (tMeshes_offset[0]);
            default:
                break;
        }
        return (null);
    }
    checkLineLength() {
        if (this.mPoints.length < 3) {
            return (false);
        }
        let tminLength = 1;
        for (let i = 0; i < this.mPoints.length - 1; i++) {
            const tP0 = this.mPoints[i];
            const tP1 = this.mPoints[i + 1];
            const tDist = Vector3.Distance(tP0, tP1);
            tminLength = Math.min(tDist, tminLength);
        }
        const tPF = this.mPoints[0];
        const tPL = this.mPoints[this.mPoints.length - 1];
        const tDistFL = Vector3.Distance(tPF, tPL);
        tminLength = Math.min(tDistFL, tminLength);
        const tRes = !Util.numbersEqual(tminLength, 0, 0.00001);
        // const tRes =  tminLength < 0.01 ? false : true;
        return (tRes);
    }
    createLine(pParent) {
        if (this.mPoints.length === 0)
            return (null);
        if (this.mClosedPolygon) {
            if (!this.checkLineLength()) {
                return (this.drawOutline(pParent));
            }
            let tMesh = this.createCSG(pParent);
            if (tMesh == null) {
                this.drawOutline(pParent);
            }
            return (tMesh);
        }
        return (this.drawOutline(pParent));
    }
    // tslint:disable-next-line:cyclomatic-complexity
    createCornerAngles() {
        if (this.mRoomElement.type === "Line" && this.mRoomElement.GridRE_REF != null) {
            return;
        }
        let taddedAngles = 0;
        if (this.mRoomElement.isTransforming || this.mRoomElement.node == null ||
            (!this.mRoomElement.isSelected && !this.mRoomElement.isDrawingNew)) {
            return;
        }
        const tmeshName = RoomManager.ROOM_SHAPE2D_PREFIX + this.mRoomElement.type.toString();
        let tHelpLines = new Array();
        tHelpLines = [];
        let tWM = new Matrix();
        try {
            tWM = this.mRoomElement.node.computeWorldMatrix();
        }
        catch (pError) {
            Log.error("Failed to get world Matrix from node.", pError);
            return;
        }
        for (let tplane of this.mTextPlanes) {
            if (tplane != null) {
                tplane.dispose(undefined, true);
            }
        }
        this.mTextPlanes = [];
        for (let tEAD of this.mPointAngles) {
            let tbiSecPoint = tEAD.CornerBiSector.scale(0.15);
            let tcurrPoint = this.mPoints[tEAD.index].add(tbiSecPoint);
            tHelpLines.push([tcurrPoint, tcurrPoint.add(tbiSecPoint)]);
            if (tEAD.node == null) {
                tEAD.node = new TransformNode("lineArcNode", this.mScene);
            }
            const twmPos = Vector3.TransformCoordinates(tcurrPoint, tWM);
            tEAD.node.setAbsolutePosition(twmPos);
            updateFrozenWorldMatrix(tEAD.node, true);
            const tScaleFactor = Util.getScaleFactor(this.mRoomManager, tEAD.node, 12); // this.getScaleFactor();
            if (this.mCornerAngleData == null ||
                this.mCornerAngleData.length === 0 ||
                tEAD.index > this.mCornerAngleData.length - 1) {
                continue;
            }
            let tEAData = this.mCornerAngleData[tEAD.index];
            if (tEAData.TextPlane == null) {
                // continue;
            }
            let tPos = Vector3.TransformCoordinates(tEAD.Origin, tWM);
            const tdegree = toLocaleString(tEAD.CornerAngle, this.mRoomManager.app.i18n.locale, 1, 1, false);
            if (tdegree == null) {
                continue;
            }
            if (tScaleFactor != null) {
                tEAD.radius = tScaleFactor * 0.6;
                const tarcPlane = Util.createTextPlane(this.mRoomManager.app, tdegree + '°', 22, 0.1, '#000000');
                this.mTextPlanes.push(tarcPlane);
                if (tEAD.CornerBiSector != null) {
                    tPos = tPos.add(tEAD.CornerBiSector.scale(tScaleFactor * 0.22));
                    tPos.y = 0.002;
                }
                tPos.y = 0.002;
                tarcPlane.setAbsolutePosition(tPos);
                if (tEAData.TextPlane != null) {
                    tEAData.TextPlane.dispose(undefined, true);
                    tEAData.TextPlane = null;
                }
                tEAData.TextPlane = tarcPlane;
                tEAData.TextPlane.scaling = new Vector3(tScaleFactor * 6, tScaleFactor * 6, tScaleFactor * 6);
                taddedAngles++;
            }
        }
        this.mCornerAnglesRemoved = false;
    }
    // tslint:disable-next-line:cyclomatic-complexity
    createHill(pParent) {
        const basePoints = this.mHillPoints_Base;
        const offsetPoints = this.mHillPoints_Offset;
        const height = this.mRoomElement.Params.hillHeight;
        const splineType = this.mRoomElement.Params.terrainType;
        if (this.mHillPoints_Offset == null || this.mHillPoints_Base == null ||
            basePoints == null || offsetPoints == null || splineType === 'Flat' ||
            !this.mcanCreateHill) {
            return (null);
        }
        const canRibbon = offsetPoints.length === basePoints.length;
        if (!canRibbon) {
            newError('Unstable calculation', 'expansion failed');
            return (null);
        }
        let topPolygon = [];
        let bottomPolygon = [];
        if (splineType === 'Top') {
            topPolygon = this.mHillPoints_Base;
            bottomPolygon = this.mHillPoints_Offset;
        }
        if (splineType === 'Bottom') {
            topPolygon = this.mHillPoints_Offset;
            bottomPolygon = this.mHillPoints_Base;
        }
        const curve = this.curveFunction(this.mRoomElement.Params.hillProfile);
        const steps = getSteps(curve);
        const inters = steps.map((x, i) => getInterpolatedPolygon(bottomPolygon, topPolygon, x)
            .map((y) => { y.y += curve[i] * height; return y; }));
        const side = CreateRibbon('line_hill_side', {
            pathArray: inters,
            closePath: true,
            sideOrientation: Mesh.BACKSIDE,
            updatable: true
        }, this.mScene);
        // side.convertToFlatShadedMesh();
        let positions = side.getVerticesData(VertexBuffer.PositionKind);
        if (positions != null) {
            let minX = Infinity;
            let minZ = Infinity;
            let maxX = -Infinity;
            let maxZ = -Infinity;
            let x = 0;
            let z = 0;
            let uvs = [];
            for (var i1 = 0; i1 < positions.length / 3; i1++) {
                x = positions[3 * i1];
                z = positions[3 * i1 + 2];
                minX = Math.min(x, minX);
                minZ = Math.min(z, minZ);
                maxX = Math.max(x, maxX);
                maxZ = Math.max(z, maxZ);
                uvs.push(x, z);
            }
            for (let i2 = 0; i2 < uvs.length / 2; i2++) {
                // uvs[2 * i2] = (uvs[2 * i2] - minX) / ((maxX - minX) * 0.15);
                // uvs[2 * i2 + 1] = (uvs[2 * i2 + 1] - minZ) / ((maxZ - minZ) * 0.15);
                uvs[2 * i2] = (uvs[2 * i2] - minX);
                uvs[2 * i2 + 1] = (uvs[2 * i2 + 1] - minZ);
            }
            side.updateVerticesData(VertexBuffer.UVKind, uvs);
        }
        side.parent = pParent;
        side.material = this.mRoomElement.localMaterial;
        side.receiveShadows = true;
        let bb = side.getBoundingInfo();
        this.mWidth = bb.maximum.x - bb.minimum.x;
        this.mDepth = bb.maximum.z - bb.minimum.z;
        this.mRoomElement.setWidth(this.mWidth, false);
        this.mRoomElement.setDepth(this.mDepth, false);
        this.mminPoint = new Vector3(bb.minimum.x, 0, bb.minimum.z);
        this.mmaxPoint = new Vector3(bb.maximum.x, this.mRoomElement.height, bb.maximum.z);
        return side;
    }
    createHelpLines(pParent) {
        if (this.mPoints.length === 0 || this.mPoints.length < 4)
            return;
        let tHelpLines = new Array();
        for (let i = 2; i < this.mPoints.length - 1; i++) {
            tHelpLines.push([this.mPoints[0], this.mPoints[i]]);
        }
        let tLines = CreateLineSystem("shape_helplines", { lines: tHelpLines }, this.mScene);
        tLines.parent = pParent;
        tLines.renderingGroupId = 3;
        this.mRoomManager.app.model.layers.setMeshLayer(tLines, "room:ShapeHelpLines");
        tLines.color = Color3.FromHexString("#000000");
        ShadowGenerator.disableMeshShadow(tLines);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
Line.PROP_KEY_GRID_FULL = "room:RoomElement:Line:GridFull";
Line.PROP_KEY_GRID_DIAGONAL = "room:RoomElement:Line:GridDiagonal";
Line.PROP_KEY_GRID_VERTICAL = "room:RoomElement:Line:GridVertical";
Line.PROP_KEY_GRID_HORIZONTAL = "room:RoomElement:Line:GridHorizontal";
Line.PROP_KEY_GRID_FREEFORM = "room:RoomElement:Line:GridFreeform";
Line.PROP_KEY_GRID_TOTALFULL = "room:RoomElement:Line:GridTotalFull";
Line.ADT_WIDTH = 768;
Line.ADT_HEIGHT = 768;
Line.UI_WIDTH = 90;
Line.UI_HEIGHT = 30;
Line.UI_WIDTH_W_GAP = 92;
Line.UI_HEIGHT_W_GAP = 32;
Line.UI_COLUMN = Math.floor(Line.ADT_WIDTH / Line.UI_WIDTH_W_GAP);
function offset(polygon, spacing) {
    const geoInput = vectorCoordinates2JTS(polygon);
    if (!geoInput[0].equals(geoInput[geoInput.length - 1])) {
        geoInput.push(geoInput[0]);
    }
    const geometryFactory = new jsts.geom.GeometryFactory();
    const ring = geometryFactory.createLineString(geoInput);
    const shell = geometryFactory.createPolygon(ring, []);
    const bufferParameters = new jsts.operation.buffer.BufferParameters();
    bufferParameters.setEndCapStyle(jsts.operation.buffer.BufferParameters.CAP_FLAT);
    bufferParameters.setQuadrantSegments(1);
    bufferParameters.setJoinStyle(jsts.operation.buffer.BufferParameters.JOIN_MITRE);
    const buffered = shell.buffer(spacing, bufferParameters);
    const simplified = simplify();
    const points = simplified.getCoordinates();
    if (points.length === 0) {
        return [];
    }
    if (points[0].equals(points[points.length - 1])) {
        points.pop();
    }
    return points.map((x) => new Vector3(x.x, 0, x.y));
    function simplify() {
        let a;
        let b = buffered;
        let tol = 0.01;
        const targetVertexCount = polygon.length;
        let tNumPoint = b.getNumPoints();
        while (targetVertexCount + 1 < tNumPoint) {
            a = b;
            b = jsts.simplify.TopologyPreservingSimplifier.simplify(b, tol);
            tNumPoint = b.getNumPoints();
            if (a.getNumPoints() === tNumPoint) {
                tol *= 2;
                if (tol > 50) {
                    break;
                }
            }
        }
        return b;
    }
}
function vectorCoordinates2JTS(tPoints) {
    return tPoints.map((point) => new jsts.geom.Coordinate(point.x, point.z));
}
function getSteps(points) {
    const step = 1 / (points.length - 1);
    const res = [];
    for (let i = 0; i < points.length; i++) {
        res.push(i * step);
    }
    return res;
}
function getInterpolatedPolygon(a, b, n) {
    const res = [];
    for (let i = 0; i < a.length; i++) {
        res.push(b[i].subtract(a[i]).scale(n).add(a[i]));
    }
    return res;
}
